/**
 * Draws the lab's own bucket grid on the chart's pens. The grid is copied onto
 * each pen rather than referenced, so the switch means "show me this grid" and
 * not "follow the lab from now on".
 */
import { useLingui } from '@lingui/react/macro'
import { AggOp, ExperimentConfig } from 'features/timeSeries/experiment/types'
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { ResampleAggregation, ResampleConfig } from 'types'

// Lab aggregation to a bucket op the read endpoint understands. Null where it
// has no equivalent, rather than substituting a mean the lab never computed.
const BUCKET_OPS: Record<AggOp, ResampleAggregation | null> = {
  mean: 'avg',
  min: 'min',
  max: 'max',
  median: null,
  last_value: null,
}

export interface LabBucketsOnChart {
  showTrainingBuckets: boolean
  setShowTrainingBuckets: (show: boolean) => void
  /** Set when the lab's grid cannot be reproduced on the chart. */
  unsupportedReason: string | null
}

export const useLabBucketsOnChart = (
  config: ExperimentConfig,
  resampleConfigs: Record<string, ResampleConfig>,
  setResampleConfigs: Dispatch<SetStateAction<Record<string, ResampleConfig>>>
): LabBucketsOnChart => {
  const { t } = useLingui()

  const tags = useMemo(
    () =>
      Array.from(
        new Set([config.target, ...config.drivers].filter((tag) => tag !== ''))
      ),
    [config.target, config.drivers]
  )

  const aggregationOp = BUCKET_OPS[config.aggregation_op]

  const grid = useMemo(
    () =>
      aggregationOp === null
        ? null
        : {
            interval: config.interval,
            intervalUnit: config.interval_unit,
            aggregationOp,
          },
    [aggregationOp, config.interval, config.interval_unit]
  )

  const showTrainingBuckets =
    grid !== null &&
    tags.length > 0 &&
    tags.every((tag) => {
      const existing = resampleConfigs[tag]
      return (
        existing?.enabled === true &&
        existing.interval === grid.interval &&
        existing.intervalUnit === grid.intervalUnit &&
        existing.aggregationOp === grid.aggregationOp
      )
    })

  const setShowTrainingBuckets = useCallback(
    (show: boolean) => {
      if (grid === null) return

      setResampleConfigs((previous) => {
        const next = { ...previous }
        tags.forEach((tag) => {
          if (!show) {
            // disabled rather than deleted, so a hand-configured pen keeps its grid
            if (next[tag]) next[tag] = { ...next[tag], enabled: false }
            return
          }
          next[tag] = {
            type: 'resample',
            sensorId: tag,
            enabled: true,
            ...grid,
          }
        })
        return next
      })
    },
    [grid, setResampleConfigs, tags]
  )

  return {
    showTrainingBuckets,
    setShowTrainingBuckets,
    unsupportedReason:
      aggregationOp === null
        ? t`The chart's bucket read has no ${{ value100_46: config.aggregation_op }} aggregation, so this grid cannot be drawn.`
        : null,
  }
}
